import { Stack } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { DateTime } from "luxon";

import { ISOStringToLocalReadableTimestamp } from "utils";

import {
  buildPublicShipmentStatusListFromETAs,
  buildPublicShipmentStatusListFromShipmentLifecycle,
} from "./utils";

import type { PublicShipmentStatus } from "./types";

interface Props {
  shipment: PublicShipment;
}

interface RowProps {
  status: PublicShipmentStatus;
}

function StatusRow(props: RowProps) {
  const { status } = props;
  return (
    <TableRow>
      <TableCell>{status.type}</TableCell>
      <TableCell>
        {status.createdAt
          ? ISOStringToLocalReadableTimestamp(status.createdAt)
          : "-"}
      </TableCell>
      <TableCell>
        {status.timestamp
          ? ISOStringToLocalReadableTimestamp(status.timestamp)
          : "NA"}
      </TableCell>
      <TableCell>{status.notes}</TableCell>
      <TableCell>{status.routePointNumber ?? ""}</TableCell>
      <TableCell>{status.createdBy}</TableCell>
    </TableRow>
  );
}

export function PublicShipmentStatusHistoryTable(props: Props) {
  const { shipment } = props;

  const lifecycleStatuses =
    buildPublicShipmentStatusListFromShipmentLifecycle(shipment);
  const etaStatuses = buildPublicShipmentStatusListFromETAs(shipment.etas);

  const combined = lifecycleStatuses.concat(etaStatuses);

  if (shipment.info.booked_at && shipment.supplier) {
    combined.push({
      type: "Shipment Booked",
      createdAt: shipment.info.booked_at,
      createdBy: `${shipment.supplier.user.first_name ?? "-"} ${shipment.supplier.user.last_name ?? "-"}`,
      notes: "",
      routePointNumber: null,
      timestamp: shipment.info.booked_at || null,
    });
  }

  combined.sort((a, b) => {
    const dateA = DateTime.fromISO(a.createdAt).toMillis();
    const dateB = DateTime.fromISO(b.createdAt).toMillis();
    return dateB - dateA;
  });

  return (
    <Stack mt="1rem">
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Provided Timestamp</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Route Point</TableCell>
              <TableCell>Created By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {combined.map((status) => (
              <StatusRow
                key={`${status.type}-${status.createdAt}`}
                status={status}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
